

import React, { useRef, useState, useEffect } from "react";
import { ArrowLeft, Pause, Play, StopCircle, Loader, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const audioUrl =
    "https://mura-ai-images-prod.s3.eu-west-1.amazonaws.com/Uploads/Template/Calibration+Media.wav";

export default function Calibration() {
    const navigate = useNavigate();
    const audioRef = useRef(new Audio(audioUrl));
    const [isPlaying, setIsPlaying] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [error, setError] = useState(null); 
    const [isImageLoading, setIsImageLoading] = useState(true); 
    const [isModalOpen, setIsModalOpen] = useState(false); 

    useEffect(() => {
        const audio = audioRef.current;
        audio.preload = 'auto';
        audio.crossOrigin = 'anonymous'; 

        const handleLoadError = () => setError("Failed to load audio. Check URL or network.");
        audio.addEventListener('error', handleLoadError);
        audio.addEventListener('loadstart', () => setError(null)); 

        return () => {
            audio.removeEventListener('error', handleLoadError);
        };
    }, []);

    const stopAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsPlaying(false);
            setIsPaused(false);
            setError(null);
            audioRef.current.onended = null;
        }
    };

    const handleStartCalibration = () => {
        const audio = audioRef.current;
        setError(null); 
        audio.load(); 

        audio.play()
            .then(() => {
                setIsPlaying(true);
                setIsPaused(false);
                setError(null);
            })
            .catch((err) => {
                console.error("Audio playback failed:", err);
                setError("Playback failed. Please check your browser settings or try refreshing.");
            });

        audio.onended = () => {
            setIsPlaying(false);
            setIsPaused(false);
        };
    };

    const handlePauseResume = () => {
        const audio = audioRef.current;
        if (isPaused) {
            audio.play()
                .then(() => {
                    setIsPaused(false);
                    setIsPlaying(true);
                })
                .catch((err) => {
                    console.error("Audio resume failed:", err);
                    setError("Resume failed. Please try starting again.");
                });
        } else {
            audio.pause();
            setIsPaused(true);
            setIsPlaying(false);
        }
    };

    const handleStop = () => {
        stopAudio();
    };

    const handleImageClick = () => {
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    useEffect(() => {
        return () => {
            stopAudio();
        };
    }, []);

    return (
        <>
            <div className="min-h-screen bg-black text-white p-2 sm:p-2">
                <main className="mx-auto">
                    <div className="bg-black rounded-lg p-2 sm:p-4">
                        <div className="flex flex-col lg:flex-row gap-6 items-left">
                            <aside className="flex-1">
                                <div className="flex flex-row items-center gap-4 mb-4">
                                    <button
                                        aria-label="go back"
                                        className="p-2 rounded-full hover:bg-gray-800"
                                        onClick={() => {
                                            stopAudio();
                                            navigate('/audio-calibaration');
                                        }}
                                    >
                                        <ArrowLeft className="w-6 h-6" />
                                    </button>

                                    <h1 className="text-lg sm:text-xl font-semibold">Calibration</h1>
                                </div>
                                <div className="mb-4 text-md sm:text-xl text-gray-200">
                                    <h2 className="font-medium mb-6">Audio Recording Level Calibration</h2>
                                </div>

                                <div className="mb-4 text-sm sm:text-base text-gray-200 flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
                                    <h2 className="font-medium">
                                        1. Click the "Start Calibration" to play "Calibration Media."
                                    </h2>

                                    <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                                        <button
                                            type="button"
                                            onClick={handleStartCalibration}
                                            disabled={isPlaying || isPaused}
                                            className={`px-3 py-1.5 rounded-md font-medium text-white
                                                      ${(isPlaying || isPaused)
                                                    ? "bg-gray-600 cursor-not-allowed"
                                                    : "bg-blue-600 hover:bg-blue-700"
                                                }`}
                                        >
                                            Start Calibration
                                        </button>
                                        {(isPlaying || isPaused) && (
                                            <div className="flex flex-wrap gap-2 items-center">
                                                <button
                                                    className="bg-yellow-600 hover:bg-yellow-700 px-3 py-1.5 rounded-md font-medium text-white flex items-center gap-1"
                                                    type="button"
                                                    onClick={handlePauseResume}
                                                    title={isPaused ? "Resume" : "Pause"}
                                                >
                                                    {isPaused ? (
                                                        <>
                                                            <Play className="w-4 h-4" />
                                                            Resume
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Pause className="w-4 h-4" />
                                                            Pause
                                                        </>
                                                    )}
                                                </button>
                                                <button
                                                    className="bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded-md font-medium text-white flex items-center gap-1"
                                                    type="button"
                                                    onClick={handleStop}
                                                    title="Stop"
                                                >
                                                    <StopCircle className="w-4 h-4" />
                                                    Stop
                                                </button>
                                            </div>
                                        )}

                                        {error && (
                                            <p className="text-red-400 text-sm mt-2">{error}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="mb-4 text-sm sm:text-base text-gray-200">
                                    <h2 className="mb-4">
                                        2. Adjust the knob on the audio recording interface to keep the indication light on the Inputs panel remains in the green zone at the -12dB position to ensure the accuracy of audio recording, as shown in photo below.
                                    </h2>
                                </div>

                                <section className="flex-shrink-0 w-full lg:w-3/5 mb-4">
                                    <div className="relative bg-gray-900 border border-gray-700 rounded overflow-hidden cursor-pointer" onClick={handleImageClick}>
                                       
                                        {isImageLoading && (
                                            <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80">
                                                <Loader className="w-8 h-8 animate-spin text-white" />
                                            </div>
                                        )}
                                        <img
                                            src="image_a.png"
                                            alt="calibration device"
                                            className="w-full h-auto object-contain max-h-[420px] sm:max-h-[360px] lg:max-h-[420px]"
                                            onLoad={() => setIsImageLoading(false)}
                                            onError={() => setIsImageLoading(false)}
                                        />
                                    </div>
                                </section>

                                <div className="mb-4 text-sm sm:text-base text-gray-200 flex items-center gap-3 flex-wrap">
                                    <h2 className="font-medium">
                                        3. Click the "Done and start detection" when level calibration has been completed.
                                    </h2>
                                    <button
                                        className={`px-3 py-1.5 rounded-md font-medium text-white ${isPlaying || isPaused
                                                ? "bg-gray-600 cursor-not-allowed"
                                                : "bg-blue-600 hover:bg-blue-700"
                                            }`}
                                        type="button"
                                        onClick={() => {
                                            stopAudio();
                                            navigate('/audiobuzz-detection');
                                        }}
                                        disabled={isPlaying || isPaused}
                                    >
                                        Done
                                    </button>
                                </div>
                            </aside>
                        </div>
                    </div>
                </main>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
                    <div className="relative w-full max-w-4xl max-h-full">
                        <button
                            onClick={closeModal}
                            className="
                            absolute -top-[26px] -right-4
                            bg-gray-600 hover:bg-gray-700
                            rounded-full
                            p-1.5 sm:p-2
                            text-white z-10
                            "
                        >
                            <X
                                className="w-4 h-4 sm:w-6 sm:h-6"
                                strokeWidth={3}
                            />
                        </button>
                        <img
                            src="image.png"
                            alt="calibration device - enlarged"
                            className="w-full h-auto max-h-[90vh] object-contain rounded"
                            onClick={closeModal} 
                        />
                    </div>
                </div>
            )}
        </>
    );
}